<template>
  <div id="MyHealth">
    <div
      class="my-health-top d-flex justify-content-between align-items-center gap-4 position-relative"
    >
      <div
        v-if="isAlertShown"
        class="my-health-alert"
        :class="isAlertSuccessful ? 'success-bg' : 'error-bg'"
      >
        <i
          v-show="isAlertSuccessful"
          class="fa fa-check-circle"
          aria-hidden="true"
        ></i>
        <p class="alert-message">{{ alertMessage }}</p>
        <i @click="closeAlert" class="fa fa-times" aria-hidden="true"></i>
      </div>
      <h2 class="brand-highlight-3 my-health-heading">My Health</h2>
      <div class="d-flex justify-content-center align-items-center">
        <b-button @click.stop="showBookings()" variant="nav">
          <span class="btn-text mr-3">My Sessions</span>
          <img src="/img/my-health/calendar.png" class="btn-img" />
        </b-button>
        <b-button
          @click.stop="showNotifications()"
          variant="nav"
          :class="{ 'unseen-notifications': unseenNotifications }"
          class="position-relative"
        >
          <span class="btn-text mr-3">Notifications</span>
          <img src="/img/my-health/bell.png" class="btn-img" />
        </b-button>
      </div>
    </div>
    <BookingList v-if="showBookingsView" @close-bookings="showBookings" />
    <NotificationList
      v-if="showNotificationsView"
      @close-notifications="showNotifications"
    />
    <div v-if="nearestUpcomingBookig" class="nearest-booking">
      <p class="nearest-booking-title brand-highlight-3">Your next session</p>
      <BookingCard
        :booking="nearestUpcomingBookig"
        :isUpcomingBooking="true"
        :isNewStyling="true"
      />
    </div>
    <div class="my-health-main">
      <MyHealthList
        v-if="services && services.length"
        :services="services"
        :handleClick="handleHealthItemClick"
      />
      <p
        v-else-if="services && !services.length"
        class="message-paragraph brand-highlight-3"
      >
        There are currently no services available.
      </p>
      <Loader v-else-if="!services" />
    </div>
  </div>
</template>

<script>
import MyHealthList from "../components/MyHealth/MyHealthList.vue";
import axios from "axios";
import config from "../config/constants";
import BookingList from "../components/MyHealth/BookingList.vue";
import NotificationList from "../components/MyHealth/NotificationList.vue";
import { mapState } from "vuex/dist/vuex.common.js";
import EventBus from "@/helpers/eventbus";
import moment from "moment";
import BookingCard from "../components/DMH/Booking/BookingCard.vue";
import Loader from "@/components/Loader.vue";

export default {
  name: "MyHealth",
  components: {
    MyHealthList,
    BookingList,
    NotificationList,
    BookingCard,
    Loader
  },
  data() {
    return {
      showNotificationsView: false,
      showBookingsView: false,
      unseenNotifications: false,
      isAlertShown: false,
      alertMessage: "",
      isAlertSuccessful: null,
      nearestUpcomingBookig: null
    };
  },
  computed: {
    ...mapState(["isOverlayVisible", "services"])
  },
  mounted() {
    this.scrollToTop();
    this.getBookings();
    this.checkForNotifications();
    this.unreadInterval = setInterval(() => {
      if (!this.unseenNotifications) this.checkForNotifications();
    }, 1000 * 60);
    EventBus.$on("alert", this.showAlert);
    EventBus.$on("getBookings", this.getBookings);
  },
  beforeDestroy() {
    if (this.unreadInterval) {
      clearInterval(this.unreadInterval);
    }
    EventBus.$off("alert", this.showAlert);
    EventBus.$off("getBookings", this.getBookings);
  },
  methods: {
    scrollToTop() {
      window.scrollTo({
        top: 0,
        behavior: "smooth"
      });
    },
    async getBookings() {
      const payload = {
        user_id: this.$store.getters.user_id
      };
      const res = await axios.post(
        config.api_env + "/application/api/u/dmh/get-all-appointments",
        payload,
        config.options
      );
      if (res.data.success) {
        const bookings = res.data.appointments;
        const upcomingBookings = [];
        bookings.forEach(booking => {
          if (
            booking.appointmentStatus !== "Cancelled" &&
            booking.appointmentStatus !== 'Cancellation Request' &&
            !moment().isAfter(moment(booking.endTime))
          ) {
            upcomingBookings.push(booking);
          }
        });
        const sortedUpcomingBookings = upcomingBookings.sort((a, b) => {
          const dateA = new Date(a.startTime);
          const dateB = new Date(b.startTime);
          return dateA - dateB;
        });
        this.nearestUpcomingBookig = upcomingBookings.length > 0 ? sortedUpcomingBookings[0] : null;
      } else if (res.data.err && res.data.err.includes("3994")) {
        this.$store.dispatch("logout");
        this.$router.push("/login");
      }
    },
    handleHealthItemClick(service) {
      this.$router.push({
        name: "myHealthArea",
        params: { areaId: service?.id }
      });
    },
    showBookings() {
      this.showNotificationsView = false;
      this.showBookingsView = !this.showBookingsView;
    },
    showNotifications() {
      this.showBookingsView = false;
      this.unseenNotifications = false;
      this.showNotificationsView = !this.showNotificationsView;
    },
    async checkForNotifications() {
      const res = await this.api({
        path: "api/u/notifications/unread"
      });
      if (res.success) {
        this.unseenNotifications = res.unread_count && res.unread_count > 0;
      } else {
        this.unseenNotifications = false;
      }
    },
    closeAlert() {
      this.isAlertShown = false;
    },
    showAlert(payload) {
      this.alertMessage = payload.message;
      this.isAlertSuccessful = payload.success;
      this.isAlertShown = true;

      setTimeout(() => {
        this.isAlertShown = false;
      }, 4000);
    }
  }
};
</script>

<style lang="less">
#MyHealth {
  flex: 1 1 auto;
  min-height: 100%;
  background-color: #fff;
  padding: 0 24px 32px;
  font-family: "PlusJakartaSansBold";

  .btn-text {
    font-family: "PlusJakartaSansBold";
  }

  .message-paragraph {
    font-size: 1.6rem;
  }

  .my-health-alert {
    position: absolute;
    z-index: 100;
    width: 100%;
    top: -8px;
    left: 0;
    display: flex;
    gap: 8px;
    align-items: center;
    margin-top: 24px;
    padding: 24px;
    border-radius: 20px;
    font-family: "PlusJakartaSansBold";
    font-size: 1.6rem;
    line-height: 2.2rem;

    &.success-bg {
      background-color: #c8faeb;

      .alert-message {
        color: #028259;
      }
    }

    &.error-bg {
      background-color: #fac8c8;

      .alert-message {
        color: #820202;
      }
    }

    .alert-message {
      margin: 0;
      padding: 0;
      flex: 1 1 auto;
      margin-right: 8px;
    }

    .fa-times {
      cursor: pointer;
    }

    .fa-check-circle {
      color: #028259;
    }
  }

  .my-health-top {
    padding: 24px 0;
  }

  .my-health-heading {
    font-family: "PlusJakartaSansExtraBold";
    font-size: 2.6rem;
    margin: 0;
  }

  .btn-img {
    position: relative;
    bottom: 2px;
    width: 18px;
    height: 18px;
    object-fit: contain;
  }

  .unseen-notifications {
    position: relative;

    &::after {
      content: "";
      display: block;
      position: absolute;
      top: 15px;
      right: 24px;
      width: 8px;
      height: 8px;
      background: #fd3d3d;
      border-radius: 50%;
    }
  }

  .nearest-booking {
    max-width: 500px;

    .nearest-booking-title {
      font-family: "PlusJakartaSansRegular";
      font-size: 1.6rem;
      line-height: 2rem;
      text-transform: uppercase;
      margin-bottom: 12px;
    }
  }

  @media screen and (max-width: 650px) {
    .btn-text {
      display: none;
    }
  }
}
</style>
